/**
 * Risk Classification Assignment Service (structural risk — ORDIN 1761).
 * Per-entity PC, FA, S, rationale, autocontrol frequency. One document per entity.
 * Resolves effective criticality (with inheritance from parent when no explicit config).
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { getLevelForScore } from '../utils/riskClassificationConstants';
import { getNomenclature } from './riskClassificationNomenclatureService';

const COLLECTION = 'risk_classification_assignments';

const ENTITY_TYPES = ['department', 'room', 'bed', 'equipment'];

/** Safe doc id: one assignment per (org, entityType, entityId) */
function assignmentDocId(organizationId, entityType, entityId) {
  const safe = (s) => String(s ?? '').replace(/\//g, '_');
  return `org_${safe(organizationId)}_${entityType}_${safe(entityId)}`;
}

/**
 * Get assignment for one entity. Returns null if none.
 * @param {string} organizationId
 * @param {string} entityType - 'department' | 'room' | 'bed' | 'equipment'
 * @param {string} entityId
 * @returns {Promise<object|null>}
 */
export async function getAssignment(organizationId, entityType, entityId) {
  if (!organizationId || !entityType || !entityId) return null;
  if (!ENTITY_TYPES.includes(entityType)) return null;
  try {
    const id = assignmentDocId(organizationId, entityType, entityId);
    const ref = doc(db, COLLECTION, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      id: snap.id,
      organizationId: data.organizationId,
      entityType: data.entityType,
      entityId: data.entityId,
      pc: data.pc,
      fa: data.fa,
      s: data.s,
      riskAssessmentRationale: data.riskAssessmentRationale ?? '',
      autocontrolFrequencyCode: data.autocontrolFrequencyCode ?? null,
      additionalMitigationCommitments: data.additionalMitigationCommitments ?? null,
      configuredBy: data.configuredBy ?? null,
      configuredAt: data.configuredAt ?? null
    };
  } catch (err) {
    console.error('[riskClassificationAssignmentService] getAssignment:', err);
    throw err;
  }
}

/**
 * Save (create or update) assignment for one entity.
 * @param {string} organizationId
 * @param {string} entityType
 * @param {string} entityId
 * @param {object} data - { pc, fa, s, riskAssessmentRationale, autocontrolFrequencyCode?, additionalMitigationCommitments? }
 * @param {string} userId
 */
export async function saveAssignment(organizationId, entityType, entityId, data, userId) {
  if (!organizationId || !entityType || !entityId || !userId) {
    throw new Error('organizationId, entityType, entityId and userId are required');
  }
  if (!ENTITY_TYPES.includes(entityType)) {
    throw new Error(`entityType must be one of: ${ENTITY_TYPES.join(', ')}`);
  }
  const pc = Number(data.pc);
  const fa = Number(data.fa);
  const s = Number(data.s);
  if ([1, 2, 3].indexOf(pc) === -1 || [1, 3].indexOf(fa) === -1 || [0, 1].indexOf(s) === -1) {
    throw new Error('Invalid PC (1-3), FA (1 or 3), or S (0 or 1)');
  }
  if (!data.riskAssessmentRationale || String(data.riskAssessmentRationale).trim() === '') {
    throw new Error('Risk assessment rationale is required');
  }
  try {
    const id = assignmentDocId(organizationId, entityType, entityId);
    const ref = doc(db, COLLECTION, id);
    await setDoc(ref, {
      organizationId,
      entityType,
      entityId,
      pc,
      fa,
      s,
      riskAssessmentRationale: String(data.riskAssessmentRationale).trim(),
      autocontrolFrequencyCode: data.autocontrolFrequencyCode ?? null,
      additionalMitigationCommitments: data.additionalMitigationCommitments ?? null,
      configuredBy: userId,
      configuredAt: serverTimestamp()
    }, { merge: true });
  } catch (err) {
    console.error('[riskClassificationAssignmentService] saveAssignment:', err);
    throw err;
  }
}

/**
 * Delete assignment for one entity (optional; UI may prefer "clear" = save with empty or leave as is).
 */
export async function deleteAssignment(organizationId, entityType, entityId) {
  if (!organizationId || !entityType || !entityId) return;
  const { deleteDoc } = await import('firebase/firestore');
  const id = assignmentDocId(organizationId, entityType, entityId);
  const ref = doc(db, COLLECTION, id);
  await deleteDoc(ref);
}

/**
 * Get all assignments for an organization, optionally filtered by entityType.
 * @param {string} organizationId
 * @param {string} [entityType]
 * @returns {Promise<object[]>}
 */
export async function getAssignmentsByOrganization(organizationId, entityType = null) {
  if (!organizationId) return [];
  try {
    let q = query(
      collection(db, COLLECTION),
      where('organizationId', '==', organizationId)
    );
    if (entityType && ENTITY_TYPES.includes(entityType)) {
      q = query(q, where('entityType', '==', entityType));
    }
    const snap = await getDocs(q);
    return snap.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        organizationId: data.organizationId,
        entityType: data.entityType,
        entityId: data.entityId,
        pc: data.pc,
        fa: data.fa,
        s: data.s,
        riskAssessmentRationale: data.riskAssessmentRationale ?? '',
        autocontrolFrequencyCode: data.autocontrolFrequencyCode ?? null,
        additionalMitigationCommitments: data.additionalMitigationCommitments ?? null,
        configuredBy: data.configuredBy,
        configuredAt: data.configuredAt
      };
    });
  } catch (err) {
    console.error('[riskClassificationAssignmentService] getAssignmentsByOrganization:', err);
    throw err;
  }
}

/**
 * Get parent entity for inheritance: room→department, bed→room, equipment→department. Department has no parent.
 * @param {string} entityType
 * @param {object} item - the entity (room has departmentId, bed has roomId, equipment has departmentId)
 * @param {object} structure - { departments, rooms, beds, equipment }
 * @returns {{ parentType: string, parentId: string } | null}
 */
export function getParentEntity(entityType, item, structure) {
  if (!item || !structure) return null;
  if (entityType === 'room' && item.departmentId) {
    return { parentType: 'department', parentId: item.departmentId };
  }
  if (entityType === 'bed' && item.roomId) {
    return { parentType: 'room', parentId: item.roomId };
  }
  if (entityType === 'equipment' && item.departmentId) {
    return { parentType: 'department', parentId: item.departmentId };
  }
  return null;
}

/**
 * Resolve effective criticality for an entity: explicit assignment or inherited from parent.
 * @param {string} organizationId
 * @param {string} entityType
 * @param {string} entityId
 * @param {object} structure - { departments, rooms, beds, equipment }
 * @param {object} item - the entity (for parent lookup)
 * @returns {Promise<{ score, level, inherited, assignment }>} level from nomenclature; inherited true if from parent
 */
export async function getEffectiveCriticality(organizationId, entityType, entityId, structure, item) {
  const nomenclature = await getNomenclature(organizationId);
  const levels = nomenclature?.levels ?? [];

  const resolveFromAssignment = (assignment) => {
    const score = (assignment.pc ?? 0) + (assignment.fa ?? 0) + (assignment.s ?? 0);
    const level = getLevelForScore(score, levels);
    return { score, level, assignment };
  };

  const assignment = await getAssignment(organizationId, entityType, entityId);
  if (assignment) {
    return { ...resolveFromAssignment(assignment), inherited: false };
  }

  const parent = getParentEntity(entityType, item, structure);
  if (!parent) {
    return { score: null, level: null, inherited: false, assignment: null };
  }

  const parentAssignment = await getAssignment(organizationId, parent.parentType, parent.parentId);
  if (!parentAssignment) {
    return { score: null, level: null, inherited: true, assignment: null };
  }

  const resolved = resolveFromAssignment(parentAssignment);
  return { ...resolved, inherited: true };
}

/**
 * Build risk map rows for view/export (Phase 3). One row per entity with resolved effective criticality.
 * @param {string} organizationId
 * @param {object} structure - { departments, rooms, beds, equipment, floors, buildings, locations, organizations }
 * @returns {Promise<{ rows: object[], totalEntities: number, configuredCount: number }>}
 */
export async function getRiskMapData(organizationId, structure) {
  if (!organizationId || !structure) {
    return { rows: [], totalEntities: 0, configuredCount: 0 };
  }
  const [assignments, nomenclature] = await Promise.all([
    getAssignmentsByOrganization(organizationId),
    getNomenclature(organizationId)
  ]);
  const levels = nomenclature?.levels ?? [];
  const assignmentByKey = {};
  assignments.forEach((a) => {
    assignmentByKey[`${a.entityType}:${a.entityId}`] = a;
  });

  const getAssignment = (entityType, entityId) =>
    assignmentByKey[`${entityType}:${entityId}`] || null;

  const resolveEffective = (entityType, item) => {
    const direct = getAssignment(entityType, item.id);
    if (direct) {
      const score = (direct.pc ?? 0) + (direct.fa ?? 0) + (direct.s ?? 0);
      const level = getLevelForScore(score, levels);
      return { assignment: direct, score, level, inherited: false };
    }
    const parent = getParentEntity(entityType, item, structure);
    if (!parent) return { assignment: null, score: null, level: null, inherited: false };
    const parentAss = getAssignment(parent.parentType, parent.parentId);
    if (!parentAss) return { assignment: null, score: null, level: null, inherited: true };
    const score = (parentAss.pc ?? 0) + (parentAss.fa ?? 0) + (parentAss.s ?? 0);
    const level = getLevelForScore(score, levels);
    return { assignment: parentAss, score, level, inherited: true };
  };

  const floorName = (floorId) => {
    const f = (structure.floors || []).find((x) => x.id === floorId);
    return f?.name || floorId || '';
  };
  const departmentName = (id) => (structure.departments || []).find((x) => x.id === id)?.name || id || '';
  const roomName = (id) => (structure.rooms || []).find((x) => x.id === id)?.name || id || '';

  const rows = [];
  let configuredCount = 0;

  const types = [
    { key: 'departments', entityType: 'department' },
    { key: 'rooms', entityType: 'room' },
    { key: 'beds', entityType: 'bed' },
    { key: 'equipment', entityType: 'equipment' }
  ];

  for (const { key, entityType } of types) {
    const items = structure[key] || [];
    for (const item of items) {
      const effective = resolveEffective(entityType, item);
      if (getAssignment(entityType, item.id)) configuredCount++;

      let sectionLabel = item.name || item.bedNumber || item.roomNumber || item.id || '—';
      let locationDetail = '';
      if (entityType === 'department' && item.floorId) {
        locationDetail = floorName(item.floorId);
      }
      if (entityType === 'room' && item.departmentId) {
        const dept = departmentName(item.departmentId);
        const floorId = (structure.departments || []).find((d) => d.id === item.departmentId)?.floorId;
        locationDetail = [dept, floorName(floorId)].filter(Boolean).join(', ');
      }
      if (entityType === 'bed' && item.roomId) {
        const room = roomName(item.roomId);
        const roomItem = (structure.rooms || []).find((r) => r.id === item.roomId);
        const dept = roomItem?.departmentId ? departmentName(roomItem.departmentId) : '';
        locationDetail = [room, dept].filter(Boolean).join(', ');
      }
      if (entityType === 'equipment' && item.departmentId) {
        locationDetail = departmentName(item.departmentId);
      }
      if (locationDetail) sectionLabel = `${sectionLabel}${locationDetail ? ` — ${locationDetail}` : ''}`;

      const assignment = effective.assignment;
      const autocontrolCode = assignment?.autocontrolFrequencyCode || effective.level?.defaultFrequencyCode || null;

      rows.push({
        entityType,
        entityId: item.id,
        sectionLabel: sectionLabel.trim(),
        sectionDetail: locationDetail,
        riskScore: effective.score,
        riskLevel: effective.level,
        inherited: effective.inherited,
        riskAssessmentRationale: assignment?.riskAssessmentRationale ?? null,
        autocontrolFrequencyCode: autocontrolCode,
        additionalMitigationCommitments: assignment?.additionalMitigationCommitments ?? null,
        hasExplicitAssignment: !!getAssignment(entityType, item.id)
      });
    }
  }

  const totalEntities = rows.length;
  return { rows, totalEntities, configuredCount };
}

export const riskClassificationAssignmentService = {
  getAssignment,
  saveAssignment,
  deleteAssignment,
  getAssignmentsByOrganization,
  getParentEntity,
  getEffectiveCriticality,
  getRiskMapData,
  ENTITY_TYPES
};
